import React, { useEffect, useState } from 'react';
import Chart, {
  ArgumentAxis,
  ValueAxis,
  Series,
  Legend,
  Title,
  Tooltip,
  Export,
  Animation
} from 'devextreme-react/chart';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { PerformanceServices } from "./PerformanceServices";
import Swal from 'sweetalert2';
import { Button, Spinner } from 'react-bootstrap';

const OccupationHistogram = ({ IdCluster }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [monthsData, setMonthsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [capacityData, setCapacityData] = useState([]);

  const RESOURCE_COL_WIDTH = 200;
  const MONTH_COL_WIDTH = 80;

  const monthNames = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  const pad = (n) => String(n).padStart(2, "0");
  const formatNumber = (num) => Number(num || 0).toFixed(1);

  const getOccupationColor = (percentage) => {
    if (percentage >= 0 && percentage < 31) return "#008700";
    if (percentage >= 31 && percentage < 61) return "#e8e812ff";
    if (percentage >= 61 && percentage < 91) return "#FFA400";
    if (percentage >= 91) return "#FC0200";
    return "#7f8c8d";
  };

  const periodoKeyFromPeriodoString = (p) => {
    if (!p) return null;
    const match = String(p).match(/^(\d{4})-(\d{1,2})/);
    if (!match) return null;
    const y = match[1];
    const m = pad(Number(match[2]));
    return `${y}-${m}`; // "2025-07"
  };

  const formatPeriodoKey = (year, monthZeroBased) => `${year}-${pad(monthZeroBased + 1)}`;

  const getOccupationData = async () => {
    if (!startDate || !endDate) {
      Swal.fire("Advertencia", "Por favor selecciona ambas fechas", "warning");
      return;
    }
    if (startDate > endDate) {
      Swal.fire("Advertencia", "La fecha de inicio debe ser anterior a la fecha fin", "warning");
      return;
    }

    setLoading(true);
    try {
      const filter = {
        IdCluster,
        start_date: new Date(startDate.getFullYear(), startDate.getMonth(), 1),
        end_date: new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0),
      };

      const result = await PerformanceServices.getDataClusterHistogram(filter);
      const tasks = result?.task || [];
      const availableHours = result?.availableHours || [];

      setCapacityData(availableHours);

      const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      const monthsDiff = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
      const months = Array.from({ length: monthsDiff + 1 }, (_, i) => {
        const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
        return {
          key: formatPeriodoKey(d.getFullYear(), d.getMonth()), 
          name: `${monthNames[d.getMonth()]} ${d.getFullYear()}`,
          year: d.getFullYear(),
          month: d.getMonth(),
          date: d
        };
      });

     
      const tasksByPeriodo = tasks.reduce((acc, t) => {
        const key = periodoKeyFromPeriodoString(t.Periodo);
        if (!key) return acc;
        if (!acc.has(key)) acc.set(key, []);
        acc.get(key).push(t);
        return acc;
      }, new Map());

      const uniqueEmpleadosMap = new Map();
      tasks.forEach((t) => {
        const name = t.NombreUsuario || "";
        if (!uniqueEmpleadosMap.has(name)) {
          uniqueEmpleadosMap.set(name, { nombre: name, profesionCargo: t.ProfesionCargo || "" });
        }
      });
      const uniqueEmpleados = Array.from(uniqueEmpleadosMap.values());
      setUsers(uniqueEmpleados);

      const processedMonthsData = months.map((month) => {
        const monthTasks = tasksByPeriodo.get(month.key) || [];

        const employeeData = uniqueEmpleados.map((empleado) => {
          const employeeTasks = monthTasks.filter((t) => t.NombreUsuario === empleado.nombre);

          if (employeeTasks.length === 0) {
            return {
              empleado: empleado.nombre,
              profesionCargo: empleado.profesionCargo,
              horasPlaneadas: 0,
              horasReales: 0,
              cantidadProyectos: 0,
              pctPlaneada: 0,
              pctReal: 0,
              actividades: []
            };
          }

          const pctPlaneada = employeeTasks.reduce((s, t) => s + (parseFloat(t.PctOcupacionPlaneada) || 0), 0);
          const pctReal = employeeTasks.reduce((s, t) => s + (parseFloat(t.PctOcupacionReal) || 0), 0);
          const horasPlaneadas = employeeTasks.reduce((s, t) => s + (parseFloat(t.HorasPlaneadasFiltro) || 0), 0);
          const horasReales = employeeTasks.reduce((s, t) => s + (parseFloat(t.HorasRealesFiltro) || 0), 0);
          const proyectosTotales = employeeTasks.reduce((s, t) => s + (t.ProyectosActivos || 0), 0);

          return {
            empleado: empleado.nombre,
            profesionCargo: empleado.profesionCargo,
            horasPlaneadas,
            horasReales,
            cantidadProyectos: proyectosTotales,
            pctPlaneada,
            pctReal,
            actividades: employeeTasks
          };
        }).filter((emp) => emp.horasPlaneadas > 0 || emp.horasReales > 0);

        return {
          ...month,
          data: employeeData
        };
      });

      setMonthsData(processedMonthsData);
    } catch (error) {
      console.error("Error fetching occupation data:", error);
      Swal.fire("Error", "Error al obtener los datos de ocupación", "error");
      setMonthsData([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Buscar (botón)
  const handleSearch = () => {
    getOccupationData();
  };


/*   useEffect(() => {
    if (startDate && endDate && IdCluster) {
      getOccupationData();
    }

  }, [IdCluster, startDate, endDate]); */

  const getDateRangeText = () => {
    if (!startDate || !endDate) return "";
    const startMonth = monthNames[startDate.getMonth()];
    const endMonth = monthNames[endDate.getMonth()];
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();

    if (startYear === endYear) {
      if (startMonth === endMonth) return `${startMonth} ${startYear}`;
      return `${startMonth} - ${endMonth} ${startYear}`;
    }
    return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
  };

  const needsHorizontalScroll = monthsData.length > 4;
  const minTableWidthPx = RESOURCE_COL_WIDTH + monthsData.length * MONTH_COL_WIDTH * 4;

  return (
    <div style={{ padding: '20px', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '30px', textAlign: 'center' }}>
          <h1 style={{ margin: '0 0 10px 0', color: '#E0301E', fontSize: '28px', fontWeight: '600' }}>
            Histograma de Ocupación por Mes
          </h1>
          <p style={{ color: '#7f8c8d', margin: 0, fontSize: '16px' }}>
            Horas proporcionales por mes de cada recurso
          </p>
        </div>

        {/* Filtros */}
        <div style={{
          display: 'flex',
          gap: '20px',
          alignItems: 'flex-end',
          marginBottom: '30px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontWeight: '500', color: '#495057', fontSize: '14px' }}>Fecha Inicio:</label>
            <DatePicker
              selected={startDate}
              onChange={(date) => date && setStartDate(date)}
              dateFormat="MM/yyyy"
              showMonthYearPicker
              className="form-control"
              maxDate={endDate}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontWeight: '500', color: '#495057', fontSize: '14px' }}>Fecha Fin:</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => date && setEndDate(date)}
              dateFormat="MM/yyyy"
              showMonthYearPicker
              className="form-control"
              minDate={startDate}
            />
          </div>
          <Button
            className="ml-2"
            variant="primary"
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                Cargando...
              </>
            ) : (
              <>
                <i className="fa fa-search mr-2" />
                Buscar
              </>
            )}
          </Button>
        </div>

        {/* Tabla dinámica */}
        {monthsData.length > 0 && users.length > 0 && (
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#E0301E', fontSize: '24px', fontWeight: '600' }}>
              Ocupación por Periodo: {getDateRangeText()}
            </h2>

            <div style={{
              overflowX: needsHorizontalScroll ? 'auto' : 'hidden',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <table style={{
                width: needsHorizontalScroll ? 'auto' : '100%',
                minWidth: needsHorizontalScroll ? `${minTableWidthPx}px` : '100%',
                borderCollapse: 'collapse',
                fontSize: '13px',
                tableLayout: needsHorizontalScroll ? 'auto' : 'fixed'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th rowSpan={3} style={{
                      padding: '12px',
                      textAlign: 'left',
                      minWidth: `${RESOURCE_COL_WIDTH}px`,
                      position: 'sticky',
                      left: 0,
                      background: '#f8f9fa',
                      zIndex: 10,
                      borderRight: '1px solid #dee2e6',
                      fontWeight: '600',
                      width: '200px',
                    }}>
                      Recurso
                    </th>
                    {monthsData.map((month, monthIndex) => (
                      <th key={month.key}
                        colSpan={4}
                        style={{
                          padding: '12px',
                          textAlign: 'center',
                          minWidth: `${MONTH_COL_WIDTH * 4}px`,
                          backgroundColor: monthIndex % 2 === 0 ? '#ffffff' : '#f1f3f4',
                          borderLeft: monthIndex > 0 ? '2px solid #dee2e6' : 'none',
                          fontWeight: '600'
                        }}>
                        {month.name}
                      </th>
                    ))}
                  </tr>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    {[...capacityData]
                      .sort((a, b) => a.Periodo.localeCompare(b.Periodo)) // ordenar por Periodo ascendente
                      .map((data, index) => (
                        <th
                          key={`${data.Periodo}-subtitle`}
                          colSpan={4}
                          style={{
                            padding: '4px 8px',
                            textAlign: 'center',
                            fontSize: '11px',
                            color: '#6c757d',
                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f1f3f4',
                            borderLeft: index > 0 ? '2px solid #dee2e6' : 'none',
                            fontWeight: '400',
                            fontStyle: 'italic'
                          }}
                        >
                          <strong>{data.HorasCapacidad}</strong> horas hábiles / <strong>{data.DiasCapacidad}</strong> días hábiles
                        </th>
                      ))}
                  </tr>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    {monthsData.map((month, monthIndex) => (
                      <React.Fragment key={`${month.key}-headers`}>
                        <th style={{
                          padding: '8px', textAlign: 'center', color: '#29B9D0', minWidth: `${MONTH_COL_WIDTH}px`,
                          backgroundColor: monthIndex % 2 === 0 ? '#ffffff' : '#f1f3f4', borderLeft: monthIndex > 0 ? '2px solid #dee2e6' : 'none', fontWeight: '500'
                        }}>
                          H. Planeadas
                        </th>
                        <th style={{
                          padding: '8px', textAlign: 'center', color: '#6AB41B', minWidth: `${MONTH_COL_WIDTH}px`,
                          backgroundColor: monthIndex % 2 === 0 ? '#ffffff' : '#f1f3f4', fontWeight: '500'
                        }}>
                          H. Reales
                        </th>
                        <th style={{
                          padding: '8px', textAlign: 'center', color: '#000000', minWidth: `${MONTH_COL_WIDTH}px`,
                          backgroundColor: monthIndex % 2 === 0 ? '#ffffff' : '#f1f3f4', fontWeight: '500'
                        }}>
                          Proyectos
                        </th>
                        <th style={{
                          padding: '8px', textAlign: 'center', color: '#000000', minWidth: `${MONTH_COL_WIDTH}px`,
                          backgroundColor: monthIndex % 2 === 0 ? '#ffffff' : '#f1f3f4', fontWeight: '500'
                        }}>
                          Ocupación
                        </th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((emp, empIndex) => (
                    <tr key={emp.nombre} style={{ backgroundColor: empIndex % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                      <td style={{
                        padding: '12px', fontWeight: '500', minWidth: `${RESOURCE_COL_WIDTH}px`, position: 'sticky',
                        left: 0, background: empIndex % 2 === 0 ? '#ffffff' : '#f8f9fa', zIndex: 5, borderRight: '1px solid #dee2e6'
                      }}>
                        <div>
                          <span>{emp.nombre}</span>
                          {emp.profesionCargo && (
                            <span style={{ fontStyle: 'italic', color: '#6c757d', fontSize: '11px', display: 'block', marginTop: '2px' }}>
                              ({emp.profesionCargo})
                            </span>
                          )}
                        </div>
                      </td>
                      {monthsData.map((month, monthIndex) => {
                        const empData = month.data.find(d => d.empleado === emp.nombre);
                        const cellStyle = {
                          textAlign: 'center',
                          minWidth: `${MONTH_COL_WIDTH}px`,
                          backgroundColor: monthIndex % 2 === 0 ? '#ffffff' : '#f8f9fa',
                          padding: '10px 8px',
                          fontSize: '12px',
                          borderLeft: monthIndex > 0 ? '1px solid #dee2e6' : 'none'
                        };

                        return (
                          <React.Fragment key={`${emp.nombre}-${month.key}`}>
                            <td style={cellStyle}>
                              <span style={{ color: '#29B9D0', fontWeight: '500' }}>
                                {empData && empData.horasPlaneadas > 0 ? formatNumber(empData.horasPlaneadas) : '-'}
                              </span>
                            </td>
                            <td style={cellStyle}>
                              <span style={{ color: '#6AB41B', fontWeight: '500' }}>
                                {empData && empData.horasReales > 0 ? formatNumber(empData.horasReales) : '-'}
                              </span>
                            </td>
                            <td style={cellStyle}>
                              <span style={{ color: '#000000', fontWeight: '500' }}>
                                {empData && empData.cantidadProyectos > 0 ? empData.cantidadProyectos : '-'}
                              </span>
                            </td>
                            <td style={cellStyle}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
                                {empData && (empData.pctPlaneada > 0 || empData.pctReal > 0) ? (
                                  <>
                                    <span style={{ fontSize: '11px' }}>
                                      <span style={{ color: '#6c757d' }}>Plan.: </span>
                                      <span style={{ color: getOccupationColor(empData.pctPlaneada) }}>
                                        {empData.pctPlaneada.toFixed(0)}%
                                      </span>
                                    </span>
                                    <span style={{ fontSize: '11px' }}>
                                      <span style={{ color: '#6c757d' }}>Real: </span>
                                      <span style={{ color: getOccupationColor(empData.pctReal) }}>
                                        {empData.pctReal.toFixed(0)}%
                                      </span>
                                    </span>
                                  </>
                                ) : (
                                  <span>-</span>
                                )}
                              </div>
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Gráficos */}
        {monthsData.length > 0 && monthsData.map((monthInfo) => {
          const monthDataWithData = monthInfo.data.filter(emp =>
            emp.horasPlaneadas > 0 || emp.horasReales > 0
          );

          if (monthDataWithData.length === 0) return null;

          const chartData = monthDataWithData.map(emp => ({
            empleado: emp.empleado,
            horasPlaneadas: Number(emp.horasPlaneadas.toFixed(1)),
            horasReales: Number(emp.horasReales.toFixed(1)),
            proyectos: emp.cantidadProyectos
          }));

          return (
            <div key={monthInfo.key} style={{ marginBottom: '50px' }}>
              <h3 style={{ color: '#E0301E', marginBottom: '20px' }}>
                Ocupación - {monthInfo.name}
              </h3>
              <Chart
                id={`occupation-chart-${monthInfo.key}`}
                dataSource={chartData}
                height={400}
                style={{ backgroundColor: 'white' }}
              >
                <Title
                  text={`Ocupación - ${monthInfo.name}`}
                  subtitle="Horas planeadas vs. horas reales"
                />
                <ArgumentAxis
                  argumentField="empleado"
                  discreteAxisDivisionMode="crossLabels"
                />
                <ValueAxis title="Horas" />
                <Series
                  argumentField="empleado"
                  valueField="horasPlaneadas"
                  name="Horas Planeadas"
                  type="bar"
                  color="#29B9D0"
                />
                <Series
                  argumentField="empleado"
                  valueField="horasReales"
                  name="Horas Reales"
                  type="bar"
                  color="#6AB41B"
                />
                <Legend verticalAlignment="bottom" horizontalAlignment="center" />
                <Tooltip
                  enabled={true}
                  shared={false}
                  customizeTooltip={(pointInfo) => {
                    if (!pointInfo || !pointInfo.point || !pointInfo.point.data) {
                      return { html: '<div>No hay datos disponibles</div>' };
                    }

                    const data = pointInfo.point.data;
                    const seriesName = pointInfo.point.series.name;
                    const value = pointInfo.point.value;

                    return {
                      html: `
                        <div style="padding: 10px; background: white; border: 1px solid #ccc; border-radius: 4px;">
                          <strong>${data.empleado}</strong><br/>
                          ${seriesName}: ${value}<br/>
                          Proyectos: ${data.proyectos || 0}
                        </div>
                      `
                    };
                  }}
                />
                <Export enabled={true} />
                <Animation enabled={true} />
              </Chart>
            </div>
          );
        })}

        {monthsData.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>📊</div>
            <h4>Sin datos disponibles</h4>
            <p>Selecciona un rango de fechas y haz clic en "Buscar" para ver los datos de ocupación</p>
          </div>
        )}

        {monthsData.length > 0 && users.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔍</div>
            <h4>No se encontraron recursos</h4>
            <p>No hay recursos con datos para el rango de fechas seleccionado</p>
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            <div style={{ display: 'inline-block', marginBottom: '20px' }}>
              <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
            </div>
            <h4>Cargando datos...</h4>
            <p>Procesando información de ocupación por mes...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OccupationHistogram;
